import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Combine, Files, Loader2, TriangleAlert } from "lucide-react";
import { primaryButtonClass, secondaryButtonClass } from "@/lib/ui";
import { useRecorder, type BatchFile, type PendingBatch } from "@/recorder/context";

/**
 * Several files dropped or picked at once are often one lecture recorded in
 * parts - a phone that was stopped at the break, or a memo app that splits
 * long recordings. This asks which it is, with the files already put in the
 * order they were recorded, so the answer is a glance and not a guess.
 */

/** Parts closer than this are back to back, not a gap worth showing. */
const GAP_SHOWN_MS = 60_000;

function sameDay(a: number, b: number): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function day(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** "13 min", "1 h 05 min" - a gap read as "12:00" looks like a time of day. */
function span(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")} min`;
}

function endOf(item: BatchFile): number {
  return item.startMs + (item.timing.durationMs ?? 0);
}

export default function BatchUploadDialog() {
  const recorder = useRecorder();
  return (
    <AnimatePresence>
      {recorder.batch !== null && (
        <BatchDialog
          key="batch-upload"
          batch={recorder.batch}
          onChoose={(choice) => void recorder.resolveBatch(choice)}
        />
      )}
    </AnimatePresence>
  );
}

function BatchDialog({
  batch,
  onChoose,
}: {
  batch: PendingBatch;
  onChoose: (choice: "join" | "separate" | null) => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onChoose(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onChoose]);

  const { files, probing } = batch;
  const oneDay = files.every((item) => sameDay(item.startMs, files[0].startMs));
  const guessed = files.filter((item) => !item.fromFile).length;
  const totalMs = files.reduce((sum, item) => sum + (item.timing.durationMs ?? 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => onChoose(null)}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs"
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="batch-upload-title"
        initial={{ scale: 0.95, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 10 }}
        transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.25 }}
        onClick={(event) => event.stopPropagation()}
        className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-md min-w-0 flex-col overflow-hidden rounded-2xl border border-[#d1d1cf] bg-[#fdfdfc] p-6 shadow-xl dark:border-[#2a2a28] dark:bg-[#141413]"
      >
        <div className="meniscus absolute top-0 left-0 h-[1px] w-full opacity-60" />
        <div className="flex items-center justify-between border-b border-[#d1d1cf]/60 pb-3 dark:border-[#2a2a28]">
          <span className="font-mono text-xs tracking-wider text-neutral-500 uppercase">
            {files.length} files
          </span>
          {!probing && (
            <span className="font-mono text-xs text-neutral-500">
              {oneDay && day(files[0].startMs)}
              {oneDay && totalMs > 0 && " · "}
              {totalMs > 0 && `${span(totalMs)} in all`}
            </span>
          )}
        </div>

        <div className="mt-4">
          <h3
            id="batch-upload-title"
            className="text-base font-semibold tracking-tight text-[#222] dark:text-[#ededeb]"
          >
            Parts of one recording?
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-neutral-600 dark:text-neutral-400">
            {probing
              ? "Reading when each file was recorded…"
              : "In the order they were recorded, going by the times inside the files. Joined, they become one recording with one transcript and summary."}
          </p>
        </div>

        <ol className="mt-4 -mx-1 flex min-h-0 flex-col overflow-y-auto px-1">
          {files.map((item, index) => {
            const previous = index > 0 ? files[index - 1] : null;
            const gapMs = previous === null ? 0 : item.startMs - endOf(previous);
            return (
              <li key={`${item.file.name}-${item.file.size}-${item.file.lastModified}`}>
                {!probing && previous !== null && gapMs <= -GAP_SHOWN_MS && (
                  <p className="flex items-center gap-1.5 py-1 pl-8 font-mono text-[10px] text-[#F59E0B]">
                    <TriangleAlert className="h-3 w-3 shrink-0" />
                    overlaps the part before by {span(-gapMs)}
                  </p>
                )}
                {!probing && previous !== null && gapMs >= GAP_SHOWN_MS && (
                  <p className="py-1 pl-8 font-mono text-[10px] text-neutral-400">
                    {span(gapMs)} gap
                  </p>
                )}
                <div className="flex min-w-0 items-center gap-2.5 rounded-lg py-1.5">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-neutral-900/[0.05] font-mono text-[11px] text-neutral-500 tabular-nums dark:bg-white/[0.08] dark:text-neutral-400">
                    {probing ? "·" : index + 1}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className="truncate text-xs font-medium text-[#222] dark:text-[#ededeb]"
                      title={item.file.name}
                    >
                      {item.file.name}
                    </span>
                    <span className="truncate font-mono text-[10px] text-neutral-500">
                      {probing ? (
                        "…"
                      ) : (
                        <>
                          {!oneDay && `${day(item.startMs)} · `}
                          {clock(item.startMs)}
                          {item.timing.durationMs !== null &&
                            `–${clock(endOf(item))} · ${span(item.timing.durationMs)}`}
                          {!item.fromFile && " · file date"}
                        </>
                      )}
                    </span>
                  </span>
                </div>
              </li>
            );
          })}
        </ol>

        {!probing && guessed > 0 && (
          <p className="mt-3 text-[11px] leading-relaxed text-neutral-500">
            {guessed === files.length
              ? "None of these files carry a recording time, so their modified times are used - check the order."
              : "Files marked “file date” carry no recording time, so their modified time is used - check the order."}
          </p>
        )}

        <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse sm:items-center">
          <button
            type="button"
            disabled={probing}
            onClick={() => onChoose("join")}
            className={`${primaryButtonClass} flex items-center justify-center gap-1.5`}
          >
            {probing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Combine className="h-3.5 w-3.5" />
            )}
            Join into one
          </button>
          <button
            type="button"
            disabled={probing}
            onClick={() => onChoose("separate")}
            className={`${secondaryButtonClass} flex items-center justify-center gap-1.5 py-2`}
          >
            <Files className="h-3.5 w-3.5 text-neutral-400" />
            Keep separate
          </button>
          <button
            type="button"
            onClick={() => onChoose(null)}
            className="cursor-pointer py-1 font-mono text-[11px] text-neutral-500 transition-colors hover:text-neutral-800 sm:mr-auto dark:hover:text-neutral-200"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
